<template>
  <div v-if="wishlist" :key="wishlist.id" class="favourite-add-form-wishlist">
    <div class="favourite-list" @click.prevent="toggleWishlist">
      <img
        v-if="wishlist.primaryPhoto"
        :src="getWishlistImageUrl()"
        class="img"
      />
      <img v-else src="~/assets/images/photo-icon.svg" class="img" />
      <base-checkbox :model-value="campsiteInWishlist">
        <div>
          {{ wishlist.name }}
          <span class="count"
            ><translate :tt="{ count: wishlist.itemCount }"
              >{count} sites</translate
            >
            <translate v-if="wishlist.status && !wishlist.isOwner" class="guest"
              >Guest</translate
            >
            <template v-else-if="wishlist.status === 'public'"
              >(<translate>Public</translate>)</template
            >
          </span>
        </div>
      </base-checkbox>
    </div>
    <transition name="fade">
      <div
        v-if="campsiteInWishlist"
        class="add-a-note"
        :class="{ 'show-content': context.note }"
      >
        <base-textarea
          name="note"
          :model-value="context.note"
          :placeholder="t('Need to remember anything about this site?')"
          @update:model-value="updateNote"
        />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useFavouritesStore } from '~/stores/useFavouritesStore'
import { FavouriteWishlistContext } from '../../machines/favouriteWishlist.machine'
import { WishlistFullData } from '../../types'
import { ref, watch } from 'vue'

const { t } = useI18n()

const props = defineProps<{
  wishlistMachine: any // TODO: Define the type of props.wishlist - MachineWishlist
}>()

const store = useFavouritesStore(usePinia())
const state: Ref<any> = ref(null)
const context: Ref<FavouriteWishlistContext> = ref(
  {} as FavouriteWishlistContext,
)
// Initialize as null to avoid undefined lookups before machine state is available
const wishlist = ref<WishlistFullData | null>(null)
const campsiteInWishlist = ref(false)

function getWishlistImageUrl() {
  // Early return if wishlist not yet initialized
  if (!wishlist.value) return '~/assets/images/photo-icon.svg'

  const image = wishlist.value.primaryPhoto
  return image
    ? `/_cfi/cdn-cgi/image/f=auto,fit=cover,g=auto,quality=70,w=60,h=60${image.url}`
    : '~/assets/images/photo-icon.svg'
}
const emit = defineEmits(['savedToWishlist', 'removedFromWishlist'])

/**
 * Synchronizes component state with XState machine state
 *
 * Machine state contract (from favouriteWishlist.machine):
 * - state.context.id: Wishlist ID (string, required)
 * - state.context.campsiteId: Campsite ID (string, required)
 * - state.context.hasCampsite: Whether campsite is in wishlist (boolean)
 * - state.context.note: User note for this wishlist item (string, optional)
 *
 * Called on:
 * 1. Component setup (initial state sync)
 * 2. Machine transitions (state changes)
 * 3. Store favouritesData changes (watched deeply)
 */
function syncValuesWithState() {
  try {
    // Safely extract context from machine state
    context.value = state.value?.context || {}

    // Guard against undefined context.id before store lookup
    if (context.value.id) {
      wishlist.value = store.wishlists[context.value.id] || null

      if (!wishlist.value) {
        console.error(
          `[FavouriteAddFormWishlist] Wishlist ${context.value.id} not found in store`,
        )
      }
    } else {
      console.error(
        '[FavouriteAddFormWishlist] Machine context missing required id field',
        { context: context.value },
      )
    }

    // Safely check campsite membership with null guards
    if (context.value.campsiteId && wishlist.value?.id) {
      campsiteInWishlist.value =
        !!store.favourites[context.value.campsiteId]?.wishlists?.[
          wishlist.value.id
        ]
    } else {
      campsiteInWishlist.value = false
    }
  } catch (error) {
    console.error('[FavouriteAddFormWishlist] Error in syncValuesWithState:', {
      error,
      state: state.value,
      context: context.value,
      wishlist: wishlist.value,
    })
    // Ensure component is in safe state even on error
    campsiteInWishlist.value = false
  }
}

// Initialize state from machine immediately during setup
// This prevents undefined crashes when component renders before first transition
state.value = props.wishlistMachine.state
syncValuesWithState()

watch(store.favouritesData, syncValuesWithState, { deep: true })

props.wishlistMachine.onTransition((s: any) => {
  state.value = s
  syncValuesWithState()
  if (s.matches('savedToWishlist')) {
    const wishlist = props.wishlistMachine.state.context
    emit('savedToWishlist', wishlist.id)
  }
  if (s.matches('removedFromWishlist')) {
    const wishlist = props.wishlistMachine.state.context
    emit('removedFromWishlist', wishlist.id)
  }
})

function updateNote(value: string | undefined) {
  props.wishlistMachine.send('UPDATE_NOTE', { note: value })
}

function toggleWishlist() {
  if (!props.wishlistMachine.state.context.hasCampsite) {
    props.wishlistMachine.send('SAVE_TO_WISHLIST')
  } else {
    props.wishlistMachine.send('REMOVE_FROM_WISHLIST')
  }
}
</script>

<style scoped>
.favourite-add-form-wishlist .add-a-note {
  margin: -0.5em 0 1em;
}

.favourite-add-form-wishlist :deep(textarea) {
  font-size: var(--body);
  min-height: 2em;
}

.favourite-add-form-wishlist .add-a-note.show-content :deep(textarea) {
  height: auto !important;
  field-sizing: content;
}

.favourite-add-form-wishlist .add-a-note :deep(label) {
  display: block;
  margin: 0;
}

.favourite-add-form-wishlist .add-a-note :deep(.label-wrapper) {
  display: none;
}

.favourite-list {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 0.75em;
  margin: 0 0 1em 0;
}

.favourite-list img {
  overflow: hidden;
  border-radius: 0.4em;
}

.favourite-list :deep(.base-checkbox) {
  width: 100%;
}

.favourite-list :deep(.base-checkbox label) {
  flex-direction: row-reverse;
  justify-content: space-between;
  margin: 0;
  overflow-wrap: anywhere;
  hyphens: auto;
}

.favourite-list .count {
  color: var(--colour-text-light);
  display: block;
}

.guest {
  color: white;
  background-color: var(--colour-offers);
  padding: 2px 4px;
  border-radius: 1em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-size: 12px;
}
</style>
